import React, { useEffect, useState } from "react";
import { CheckCircle2, FileSignature, Loader2, ShieldCheck } from "lucide-react";
import "./sign.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const euro = value => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(value) || 0);
const itDate = value => value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value.split("-").reverse().join("/") : value || "";

export default function SignPage({ token }) {
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", fiscal_code: "", accepted: false });
  const [busy, setBusy] = useState(false);
  const [signed, setSigned] = useState(null);

  useEffect(() => {
    (async () => {
      const response = await fetch(`${API}/public/estimates/${token}`);
      const json = await response.json().catch(() => null);
      if (!response.ok) { setError(typeof json?.detail === "string" ? json.detail : "Link non valido"); return; }
      setQuote(json);
      setSigned(json.signature || null);
      setForm(f => ({ ...f, name: f.name || json.client_name || "" }));
    })();
  }, [token]);

  async function submit(event) {
    event.preventDefault();
    setBusy(true); setError("");
    try {
      const response = await fetch(`${API}/public/estimates/${token}/sign`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form),
      });
      const json = await response.json().catch(() => null);
      if (!response.ok) throw new Error(typeof json?.detail === "string" ? json.detail : "Firma non riuscita");
      setSigned(json.signature);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  }

  if (error && !quote) return <main className="sign-shell" data-testid="sign-error-page">
    <section className="sign-card"><h1>Link non disponibile</h1><p className="sign-error" data-testid="sign-link-error">{error}</p></section>
  </main>;

  if (!quote) return <main className="sign-shell"><section className="sign-card sign-loading" data-testid="sign-loading"><Loader2 className="spin" size={22} />Caricamento dell'incarico…</section></main>;

  return <main className="sign-shell" data-testid="sign-page" style={{
    "--sign-accent": quote.palette?.accent, "--sign-ink": quote.palette?.ink,
    "--sign-soft": quote.palette?.soft, "--sign-paper": quote.palette?.paper,
  }}>
    <section className="sign-card">
      <header className="sign-head">
        <span className="sign-kicker">{[quote.brand?.title, quote.brand?.subtitle].filter(Boolean).join(" · ").toUpperCase()}</span>
        <h1>Lettera di incarico professionale</h1>
        <p>Incarico n° {quote.number} del {itDate(quote.date)}</p>
      </header>

      <div className="sign-body">
        <dl className="sign-grid">
          <div><dt>Committente</dt><dd data-testid="sign-client">{quote.client_name || "—"}</dd></div>
          <div><dt>{quote.issuer?.type === "societa" ? "Società incaricata" : "Professionista"}</dt><dd data-testid="sign-issuer-name">{quote.issuer?.entity_name || quote.professional?.name}</dd></div>
          {quote.issuer?.type === "societa" && <div><dt>Professionista responsabile della progettazione</dt><dd data-testid="sign-project-professional">{[quote.issuer.signer?.title, quote.issuer.signer?.name].filter(Boolean).join(" ")}</dd></div>}
          <div className="wide"><dt>Oggetto dell'incarico</dt><dd data-testid="sign-subject">{quote.subject || "—"}</dd></div>
          <div><dt>Comune</dt><dd>{quote.municipality || "—"}</dd></div>
          <div><dt>Ubicazione</dt><dd>{quote.site_address || "—"}</dd></div>
        </dl>

        <h2>Prestazioni comprese</h2>
        <ul className="sign-items" data-testid="sign-items">
          {quote.items.map(item => <li key={item.key || item.label}><span>{item.label}</span><strong>{euro(item.total)}</strong></li>)}
        </ul>

        <div className="sign-totals">
          <div><span>Onorario netto</span><strong data-testid="sign-net">{euro(quote.net_total)}</strong></div>
          <div><span>Contributo previdenziale 4%</span><strong>{euro(quote.cassa)}</strong></div>
          <div><span>I.V.A. 22%</span><strong>{euro(quote.vat)}</strong></div>
          {!!quote.expenses_total && <div><span>Spese escluse da IVA e Cassa</span><strong>{euro(quote.expenses_total)}</strong></div>}
          <div className="grand"><span>Totale documento</span><strong data-testid="sign-total">{euro(quote.document_total)}</strong></div>
          {!!quote.advance_amount && <div><span>Acconto all'avvio</span><strong data-testid="sign-advance">{euro(quote.advance_amount)}</strong></div>}
        </div>

        {quote.installments_enabled && !!quote.installments.length && <>
          <h2>Piano rate</h2>
          <ul className="sign-items" data-testid="sign-installments">
            {quote.installments.map(row => <li key={row.label}><span>{row.label}</span><strong>{euro(row.gross_amount)}</strong></li>)}
          </ul>
        </>}

        <a className="sign-download" href={`${API}/public/estimates/${token}/letter/pdf`} target="_blank" rel="noopener noreferrer" data-testid="sign-download-letter">
          <FileSignature size={17} />Leggi la lettera di incarico completa (PDF)
        </a>

        {signed ? <div className="sign-done" data-testid="sign-confirmation">
          <CheckCircle2 size={26} />
          <div>
            <strong>Incarico accettato e firmato</strong>
            <p>{signed.name} · C.F. {signed.fiscal_code}</p>
            <small>Firmato elettronicamente il {String(signed.signed_at || "").slice(0, 19).replace("T", " alle ")}. Lo studio riceverà conferma e ti contatterà per l'avvio delle attività.</small>
          </div>
        </div> : <form className="sign-form" onSubmit={submit} data-testid="sign-form">
          <h2>Accettazione e firma</h2>
          <label>Nome e cognome (o ragione sociale)
            <input required minLength={3} data-testid="sign-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          </label>
          <label>Codice fiscale / P. IVA
            <input required minLength={4} data-testid="sign-fiscal-code" value={form.fiscal_code} onChange={e => setForm({ ...form, fiscal_code: e.target.value })} />
          </label>
          <label className="sign-check">
            <input type="checkbox" required data-testid="sign-accept" checked={form.accepted} onChange={e => setForm({ ...form, accepted: e.target.checked })} />
            Dichiaro di aver letto e di accettare integralmente la lettera di incarico, i compensi indicati e le condizioni contrattuali (artt. 1-10).
          </label>
          {error && <p className="sign-error" role="alert" data-testid="sign-form-error">{error}</p>}
          <button className="sign-submit" type="submit" disabled={busy} data-testid="sign-submit">{busy ? "Invio in corso…" : "Accetta e firma l'incarico"}</button>
          <p className="sign-note"><ShieldCheck size={14} />Firma elettronica semplice: registriamo nome, codice fiscale, data e ora dell'accettazione. Link valido fino al {itDate(quote.expires)}.</p>
        </form>}
      </div>
    </section>
  </main>;
}
